import { useEffect, useRef, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import AnimateIn from "@/components/AnimateIn";
import WCButton from "@/components/WCButton";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";
import { fetchProduct } from "@/lib/shop";
import { useShop } from "@/context/ShopContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Heart, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Size ordering (Shopify sometimes returns XS last)
const SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL", "4XL"];
const sortSizes = (arr) =>
  [...arr].sort((a, b) => {
    const ai = SIZE_ORDER.indexOf(a.toUpperCase());
    const bi = SIZE_ORDER.indexOf(b.toUpperCase());
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

// Approximate hex for color-name swatch dot
const COLOR_HEX = {
  black: "#0B0B0B",
  white: "#FAFAFA",
  "oat gray": "#D9CFB5",
  "light gray": "#C8C8C8",
  "dark gray": "#3F3F3F",
  "flower gray": "#A5A29A",
  apricot: "#F4C9A8",
  "dark apricot": "#C98F68",
  coffee: "#6F4E37",
  "light green": "#9AC58B",
  "dark green": "#1B3A1B",
  "moss green": "#6B7A3A",
  "blackish green": "#1F2A23",
  "navy blue": "#0D1A3A",
  blue: "#3A5FCD",
  "dull red": "#9E3033",
  pink: "#E8A7B8",
};
const colorHex = (name) => COLOR_HEX[name.trim().toLowerCase()] || "#8A8A8A";

// Strip raw supplier spec content (size charts, hidden ids, spacer paragraphs)
// so the description only shows the brand voice + details list.
function cleanDescriptionHtml(html) {
  if (!html || typeof window === "undefined") return html || "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const root = doc.body;
    applyDescriptionCleaners(doc, root);
    return root.innerHTML;
  } catch {
    return html;
  }
}

function applyDescriptionCleaners(doc, root) {
  root
    .querySelectorAll(
      'table, div[style*="overflow"], div[id], [style*="display: none"], [style*="display:none"]'
    )
    .forEach((el) => el.remove());
  root.querySelectorAll('span[style*="font-size"]').forEach((span) => {
    const p = span.closest("p");
    if (p && p.textContent.trim().toLowerCase() === span.textContent.trim().toLowerCase()) {
      p.remove();
    } else {
      span.remove();
    }
  });
  const LABELS = ["description", "details", "size chart", "care instructions"];
  root.querySelectorAll("span").forEach((span) => {
    const raw = span.textContent.trim().replace(/[:\uFF1A]\s*$/, "");
    if (LABELS.includes(raw.toLowerCase())) {
      const strong = doc.createElement("strong");
      strong.textContent = raw;
      span.replaceWith(strong);
    }
  });
  root.querySelectorAll("span").forEach((span) => {
    if (!span.querySelector("*") && span.attributes.length === 0) {
      span.replaceWith(doc.createTextNode(span.textContent));
    }
  });
  root.querySelectorAll("p, div").forEach((el) => {
    if (!el.textContent.replace(/\u00a0/g, "").trim() && !el.querySelector("img")) {
      el.remove();
    }
  });
}

// Split cleaned description into the main description block, details <ul>, and care instructions
// so each section can render in its own accordion item.
function splitDescriptionAndDetails(html) {
  if (!html || typeof window === "undefined")
    return { main: html || "", details: "", care: "" };
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const root = doc.body;
    applyDescriptionCleaners(doc, root);

    let details = "";
    const ul = root.querySelector("ul");
    if (ul) {
      details = ul.outerHTML;
      ul.remove();
    }

    // Strip the now-orphaned "Details" heading
    root.querySelectorAll("strong").forEach((s) => {
      if (s.textContent.trim().toLowerCase().replace(/[:\uFF1A]\s*$/, "") === "details") {
        const p = s.closest("p");
        if (p && p.textContent.trim().toLowerCase().replace(/[:\uFF1A]\s*$/, "") === "details") {
          p.remove();
        } else {
          s.remove();
        }
      }
    });

    // Pull out "Care Instructions" paragraph(s)
    let care = "";
    const paragraphs = Array.from(root.querySelectorAll("p"));
    paragraphs.forEach((p) => {
      const firstText = (p.textContent || "").trim().replace(/[:\uFF1A]\s*$/, "");
      if (/^care\s*instructions?/i.test(firstText)) {
        // Clone, strip the "Care Instructions:" label, keep the rest as the body
        const clone = p.cloneNode(true);
        clone.querySelectorAll("strong, span, b").forEach((el) => {
          if (/^care\s*instructions?\s*[:：]?\s*$/i.test(el.textContent.trim())) {
            el.remove();
          }
        });
        clone.querySelectorAll("br").forEach((br, i) => {
          if (i === 0) br.remove();
        });
        care = (clone.textContent || "").trim();
        p.remove();
      }
    });

    // Clean up empty paragraphs left behind
    root.querySelectorAll("p, div").forEach((el) => {
      if (!el.textContent.replace(/\u00a0/g, "").trim() && !el.querySelector("img")) {
        el.remove();
      }
    });
    return { main: root.innerHTML, details, care };
  } catch {
    return { main: html, details: "", care: "" };
  }
}

export default function Product() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState("loading");
  const [activeImg, setActiveImg] = useState(0);
  const [selected, setSelected] = useState({}); // { color: "Black", size: "M" }
  const [adding, setAdding] = useState(false);
  const [wished, setWished] = useState(false);
  const thumbStripRef = useRef(null);
  // Only swap the main image to a variant's image once the user explicitly picks a color.
  // Before that, we show Shopify's first media image (index 0 in the gallery).
  const userPickedColor = useRef(false);
  const { addItem } = useCart();
  const { user } = useAuth();
  const { products } = useShop();

  useEffect(() => {
    setStatus("loading");
    setActiveImg(0);
    setSelected({});
    userPickedColor.current = false;
    fetchProduct(slug)
      .then((p) => {
        setProduct(p);
        setStatus("ok");
        // Default selection to the first available variant's options
        const firstAvail = (p.variants || []).find((v) => v.available) || p.variants?.[0];
        if (firstAvail) {
          const init = {};
          firstAvail.selectedOptions.forEach((o) => {
            init[o.name] = o.value;
          });
          setSelected(init);
        }
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  useEffect(() => {
    if (!user) {
      setWished(false);
      return;
    }
    api
      .get("/wishlist")
      .then((r) => setWished(r.data.some((w) => w.product_slug === slug)))
      .catch(() => {});
  }, [user, slug]);

  // Normalize a URL for comparison (strip query string)
  const normUrl = (u) => (u || "").split("?")[0];

  // Build a single stable gallery: product images + every variant image, deduped.
  // Using a stable list means indices don't shift when different variants are selected,
  // so mapping a variant to its image position stays correct.
  const gallery = (() => {
    if (!product) return [];
    const seen = new Set();
    const out = [];
    const push = (url) => {
      if (!url) return;
      const key = normUrl(url);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(url);
    };
    (product.images || []).forEach(push);
    (product.variants || []).forEach((v) => push(v.image));
    return out;
  })();

  // Switch main gallery image to match the selected variant's image (color swap).
  // Only fires after the user has explicitly picked a color — on initial load we keep
  // showing Shopify's first media image (gallery[0]) so customers see the brand/hero shot.
  const currentVariantId = product?.variants?.find((v) =>
    v.selectedOptions.every((o) => selected[o.name] === o.value)
  )?.id;
  useEffect(() => {
    if (!product) return;
    if (!userPickedColor.current) return;
    const v = (product.variants || []).find((x) => x.id === currentVariantId);
    if (!v?.image || !gallery.length) return;
    const idx = gallery.findIndex((img) => normUrl(img) === normUrl(v.image));
    if (idx >= 0) setActiveImg(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVariantId]);

  // Keep the active thumbnail in view in the horizontal strip
  useEffect(() => {
    const strip = thumbStripRef.current;
    if (!strip) return;
    const active = strip.querySelector(`[data-testid="product-thumb-${activeImg}"]`);
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [activeImg]);

  if (status === "notfound") return <Navigate to="/shop" replace />;
  if (status === "loading" || !product) {
    return (
      <div className="bg-black min-h-screen flex items-center justify-center text-white/40 wc-label text-xs">
        LOADING…
      </div>
    );
  }

  const related = products.filter((p) => p.handle !== product.handle).slice(0, 3);
  const badge =
    product.status === "sold_out"
      ? { label: "SOLD OUT", color: "#FF1744" }
      : product.badge === "limited"
        ? { label: "DROP", color: "#F5C518" }
        : { label: "CORE COLLECTION", color: "#00C853" };

  // Match current selection to a variant
  const currentVariant = (product.variants || []).find((v) => v.id === currentVariantId);

  // Is a given option value available given currently-selected OTHER options?
  const isValueAvailable = (optionName, value) =>
    (product.variants || []).some(
      (v) =>
        v.available &&
        v.selectedOptions.some((o) => o.name === optionName && o.value === value) &&
        v.selectedOptions.every(
          (o) =>
            o.name === optionName || !selected[o.name] || selected[o.name] === o.value
        )
    );

  const addToCart = async () => {
    if (!currentVariant) {
      toast.error("Select your options first.");
      return;
    }
    if (!currentVariant.available) {
      toast.error("That combination is sold out.");
      return;
    }
    setAdding(true);
    try {
      await addItem(currentVariant.id, 1);
      const labelParts = currentVariant.selectedOptions.map((o) => o.value).join(" / ");
      toast.success(`${product.name} (${labelParts}) added to cart.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to cart.");
    } finally {
      setAdding(false);
    }
  };

  const toggleWishlist = async () => {
    if (!user) {
      toast.error("Sign in to save to your wishlist.");
      return;
    }
    if (wished) {
      await api.delete(`/wishlist/${slug}`);
      setWished(false);
      toast.success("Removed from wishlist.");
    } else {
      await api.post("/wishlist", { product_slug: slug });
      setWished(true);
      toast.success("Saved to wishlist.");
    }
  };

  const canAdd = !!currentVariant && product.status !== "sold_out" && !adding;
  const hasOptions = (product.options || []).length > 0 &&
    !(product.options.length === 1 && product.sizes?.[0] === "ONE SIZE");

  // Include the variant image in the gallery if it isn't already there
  const displayImage = gallery[activeImg] || gallery[0];
  const { main: mainDescriptionHtml, details: detailsHtml, care: careText } =
    splitDescriptionAndDetails(product.descriptionHtml || "");
  const showGalleryNav = gallery.length > 1;
  const goPrev = () =>
    setActiveImg((i) => (i - 1 + gallery.length) % gallery.length);
  const goNext = () => setActiveImg((i) => (i + 1) % gallery.length);

  return (
    <div className="bg-black" data-testid="product-page">
      <section className="pt-[140px] md:pt-[180px] pb-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
          {/* GALLERY */}
          <div data-testid="product-gallery" className="min-w-0">
            <div className="relative aspect-square bg-[#0a0a0a] overflow-hidden group">
              {displayImage ? (
                <img
                  src={displayImage}
                  alt={product.name}
                  className="wc-image w-full h-full object-cover transition-opacity duration-300"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center wc-label text-white/30">
                  IMAGE COMING
                </div>
              )}
              {showGalleryNav && (
                <>
                  <button
                    type="button"
                    onClick={goPrev}
                    aria-label="Previous image"
                    data-testid="product-gallery-prev"
                    className="absolute top-1/2 left-3 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/55 backdrop-blur-sm border border-white/20 text-white opacity-0 group-hover:opacity-100 hover:bg-black/80 hover:border-white transition-all"
                  >
                    <ChevronLeft size={22} strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    aria-label="Next image"
                    data-testid="product-gallery-next"
                    className="absolute top-1/2 right-3 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/55 backdrop-blur-sm border border-white/20 text-white opacity-0 group-hover:opacity-100 hover:bg-black/80 hover:border-white transition-all"
                  >
                    <ChevronRight size={22} strokeWidth={1.5} />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 wc-label text-[10px] text-white/80 bg-black/55 px-2 py-1 border border-white/15 tracking-widest">
                    {activeImg + 1} / {gallery.length}
                  </div>
                </>
              )}
            </div>
            {showGalleryNav && (
              <div
                ref={thumbStripRef}
                className="mt-4 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 wc-thumb-strip"
                data-testid="product-thumb-strip"
              >
                {gallery.map((img, i) => (
                  <button
                    key={`${img}-${i}`}
                    onClick={() => setActiveImg(i)}
                    className={`snap-start shrink-0 w-24 md:w-28 aspect-square overflow-hidden border transition-all ${
                      activeImg === i
                        ? "border-white opacity-100"
                        : "border-transparent opacity-55 hover:opacity-90"
                    }`}
                    data-testid={`product-thumb-${i}`}
                    aria-label={`Show image ${i + 1}`}
                  >
                    <img src={img} alt="" className="wc-image w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* INFO */}
          <div data-testid="product-info">
            <div
              className="wc-label text-[10px] inline-block px-3 py-1"
              style={{ border: `1px solid ${badge.color}`, color: badge.color }}
            >
              {badge.label}
            </div>
            <div className="flex items-start justify-between gap-6 mt-5">
              <h1 className="wc-heading text-white text-5xl md:text-7xl tracking-widest">
                {product.name}
              </h1>
              <button
                onClick={toggleWishlist}
                className={`shrink-0 border p-3 transition-colors ${
                  wished
                    ? "bg-white text-black border-white"
                    : "border-white/40 text-white hover:border-white"
                }`}
                data-testid="product-wishlist-toggle"
                aria-label="Save to wishlist"
              >
                <Heart size={20} fill={wished ? "currentColor" : "none"} />
              </button>
            </div>
            <div className="text-white/80 text-xl md:text-2xl mt-3">
              ${product.price?.toFixed(2)}
            </div>

            {hasOptions && (
              <div className="mt-10 space-y-7">
                {product.options.map((opt) => {
                  const isColor = opt.name.toLowerCase() === "color";
                  const isSize = opt.name.toLowerCase() === "size";
                  // Only show option values that have at least one real Shopify variant.
                  // Shopify sometimes leaves orphan option values behind when variants are
                  // deleted (e.g. "Navy Blue"/"Blue"/"Pink" on a hoodie with no matching variant).
                  const liveValues = opt.values.filter((v) =>
                    (product.variants || []).some((variant) =>
                      variant.selectedOptions.some(
                        (o) => o.name === opt.name && o.value === v
                      )
                    )
                  );
                  if (liveValues.length === 0) return null;
                  const values = isSize ? sortSizes(liveValues) : liveValues;
                  return (
                    <div key={opt.name} data-testid={`product-option-${opt.name.toLowerCase()}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="wc-label text-xs text-white">
                          {opt.name.toUpperCase()}
                          {selected[opt.name] && (
                            <span className="text-white/50 ml-3 tracking-normal normal-case">
                              — {selected[opt.name]}
                            </span>
                          )}
                        </div>
                        {isSize && (
                          <button
                            className="wc-label text-xs text-white/60 underline"
                            onClick={() => toast.message("Standard US sizing, unisex fit.")}
                            data-testid="product-size-guide"
                          >
                            Size Guide
                          </button>
                        )}
                      </div>
                      <div
                        className={
                          isColor
                            ? "flex flex-wrap gap-2"
                            : "grid grid-cols-6 gap-2"
                        }
                      >
                        {values.map((val) => {
                          const active = selected[opt.name] === val;
                          if (isColor) {
                            return (
                              <button
                                key={val}
                                onClick={() => {
                                  userPickedColor.current = true;
                                  setSelected((s) => ({ ...s, [opt.name]: val }));
                                }}
                                className={`flex items-center gap-2 wc-label text-[11px] py-2 px-3 border transition-colors ${
                                  active
                                    ? "bg-white text-black border-white"
                                    : "border-white/30 text-white hover:border-white"
                                }`}
                                data-testid={`product-color-${val.replace(/\s+/g, "-").toLowerCase()}`}
                              >
                                <span
                                  className="w-4 h-4 border border-white/40"
                                  style={{ background: colorHex(val) }}
                                />
                                <span>{val.toUpperCase()}</span>
                              </button>
                            );
                          }
                          return (
                            <button
                              key={val}
                              onClick={() =>
                                setSelected((s) => ({ ...s, [opt.name]: val }))
                              }
                              className={`wc-label text-xs py-4 border transition-colors ${
                                active
                                  ? "bg-white text-black border-white"
                                  : "border-white/30 text-white hover:border-white"
                              }`}
                              data-testid={`product-size-${val}`}
                            >
                              {val}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-8">
              <WCButton
                onClick={addToCart}
                disabled={!canAdd}
                className="w-full"
                data-testid="product-add-to-cart"
              >
                {product.status === "sold_out"
                  ? "Sold Out"
                  : adding
                    ? "Adding…"
                    : "Add To Cart"}
              </WCButton>
            </div>

            <div className="mt-10 text-white/75 text-base leading-[1.9]">
              {mainDescriptionHtml ? (
                <div
                  className="wc-product-description"
                  dangerouslySetInnerHTML={{ __html: mainDescriptionHtml }}
                />
              ) : product.description ? (
                <div className="whitespace-pre-wrap">{product.description}</div>
              ) : (
                <div>Built heavy. Built to last. No guaranteed restock.</div>
              )}
            </div>

            <div className="mt-10">
              <Accordion type="single" collapsible>
                {detailsHtml && (
                  <AccordionItem value="details" className="border-white/20">
                    <AccordionTrigger
                      className="wc-label text-xs text-white hover:no-underline"
                      data-testid="product-details-trigger"
                    >
                      DETAILS
                    </AccordionTrigger>
                    <AccordionContent>
                      <div
                        className="wc-product-description"
                        dangerouslySetInnerHTML={{ __html: detailsHtml }}
                      />
                    </AccordionContent>
                  </AccordionItem>
                )}
                <AccordionItem value="shipping" className="border-white/20">
                  <AccordionTrigger
                    className="wc-label text-xs text-white hover:no-underline"
                    data-testid="product-shipping-trigger"
                  >
                    SHIPPING
                  </AccordionTrigger>
                  <AccordionContent className="text-white/70 text-sm leading-[1.9]">
                    Orders are typically processed and shipped within 3–7 business days. Delivery
                    times may vary depending on your location, carrier delays, holidays, and
                    product availability. Once your order ships, you will receive tracking
                    information by email. Domestic U.S. orders usually arrive within 7–14
                    business days after fulfillment. International orders vary by country and may
                    take longer due to customs.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="returns" className="border-white/20">
                  <AccordionTrigger
                    className="wc-label text-xs text-white hover:no-underline"
                    data-testid="product-returns-trigger"
                  >
                    RETURNS
                  </AccordionTrigger>
                  <AccordionContent className="text-white/70 text-sm leading-[1.9]">
                    We accept returns within 14 days of delivery for unworn, unwashed, unused
                    items in their original condition. Customers are responsible for return
                    shipping costs unless the item arrives damaged, defective, or incorrect.
                    Because each item is made/fulfilled through our production partner, we cannot
                    accept returns for wrong size selection, buyer's remorse, or items that have
                    been worn, washed, damaged, or altered. If your item arrives damaged,
                    defective, or incorrect, contact us within 7 days of delivery with your order
                    number and clear photos so we can review and make it right.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="care" className="border-white/20">
                  <AccordionTrigger
                    className="wc-label text-xs text-white hover:no-underline"
                    data-testid="product-care-trigger"
                  >
                    CARE INSTRUCTIONS
                  </AccordionTrigger>
                  <AccordionContent className="text-white/70 text-sm leading-[1.9]">
                    {careText || product.care}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section
          className="px-6 md:px-12 pb-32 border-t border-white/10 pt-24"
          data-testid="related-products"
        >
          <div className="max-w-7xl mx-auto">
            <h2 className="wc-heading text-white text-3xl md:text-5xl tracking-widest">
              YOU MIGHT ALSO LIKE
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-10 mt-12">
              {related.map((p) => (
                <ProductCard key={p.handle} product={p} />
              ))}
            </div>
            <div className="mt-16 text-center">
              <Link
                to="/shop"
                className="wc-label text-xs text-white/60 underline"
                data-testid="related-view-all"
              >
                View All Products
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
